import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getVehicleById } from '../services/vehicleService'
import './VehicleDetail.css'

export default function VehicleDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [vehicle, setVehicle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    const loadVehicle = async () => {
      try {
        setLoading(true)
        const data = await getVehicleById(id)
        console.log('Vehicle data:', data)
        setVehicle(data)
      } catch (err) {
        console.error('Error loading vehicle:', err)
        setError('Error al cargar el vehículo')
      } finally {
        setLoading(false)
      }
    }

    loadVehicle()
  }, [id])

  if (loading) {
    return (
      <div className='vehicle-detail-loading'>
        <div className='spinner'></div>
        <p>Cargando vehículo...</p>
      </div>
    )
  }

  if (error || !vehicle) {
    return (
      <div className='vehicle-detail-error'>
        <p>{error || 'Vehículo no encontrado'}</p>
        <button onClick={() => navigate('/')}>Volver al inicio</button>
      </div>
    )
  }

  // Obtener imágenes del vehículo - compatible con múltiples formatos
  let images = []

  if (vehicle.images && Array.isArray(vehicle.images) && vehicle.images.length > 0) {
    images = vehicle.images.map(img => (typeof img === 'string' ? img : img.url)).filter(url => url)
  } else if (vehicle.url_imatge) {
    images = [vehicle.url_imatge]
  } else if (vehicle.imatge_url) {
    images = [vehicle.imatge_url]
  }

  const currentImage = images[currentImageIndex] || images[0]

  const nextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % images.length)
  }

  const prevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length)
  }

  const openFullscreen = () => {
    setIsFullscreen(true)
  }

  const closeFullscreen = () => {
    setIsFullscreen(false)
  }

  const nextImageFullscreen = () => {
    setCurrentImageIndex(prev => (prev + 1) % images.length)
  }

  const prevImageFullscreen = () => {
    setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length)
  }

  return (
    <div className='vehicle-detail-page'>
      <div className='vehicle-detail-container'>
        <button className='back-button' onClick={() => navigate('/')}>
          ← Volver
        </button>

        <div className='detail-layout'>
          {/* Carousel de imágenes - lado izquierdo */}
          <div className='image-section'>
            <div className='main-image-wrapper'>
              <img src={currentImage} alt={`${vehicle.marca_nom} ${vehicle.model_nom}`} className='main-vehicle-image' />

              {/* Botones de navegación */}
              {images.length > 1 && (
                <>
                  <button className='nav-btn prev-btn' onClick={prevImage}>
                    ‹
                  </button>
                  <button className='nav-btn next-btn' onClick={nextImage}>
                    ›
                  </button>
                </>
              )}

              {/* Contador de imágenes */}
              {images.length > 1 && (
                <div className='image-counter-main'>
                  {currentImageIndex + 1}/{images.length}
                </div>
              )}

              {/* Botón fullscreen */}
              <button className='fullscreen-btn' onClick={openFullscreen}>
                ⛶
              </button>
            </div>

            {/* Miniaturas */}
            {images.length > 1 && (
              <div className='thumbnails'>
                {images.map((img, index) => (
                  <img key={index} src={img} alt={`Vista ${index + 1}`} className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`} onClick={() => setCurrentImageIndex(index)} />
                ))}
              </div>
            )}
          </div>

          {/* Información del vehículo - lado derecho */}
          <div className='info-section'>
            <div className='vehicle-header'>
              <h1 className='vehicle-brand'>
                {vehicle.marca_nom} {vehicle.model_nom}
              </h1>
              <h2 className='vehicle-version'>{vehicle.model_nom}</h2>
            </div>

            <div className='vehicle-specs'>
              <div className='spec-item'>
                <span className='spec-value'>{vehicle.any}</span>
                <span className='spec-label'>Año</span>
              </div>
              <div className='spec-item'>
                <span className='spec-value'>{vehicle.kilometratge?.toLocaleString('es-ES')} km</span>
                <span className='spec-label'>Kilometraje</span>
              </div>
              <div className='spec-item'>
                <span className='spec-value'>{vehicle.potencia} CV</span>
                <span className='spec-label'>Potencia</span>
              </div>
              <div className='spec-item'>
                <span className='spec-value'>Gasolina</span>
                <span className='spec-label'>Combustible</span>
              </div>
            </div>

            <div className='price-section'>
              <div className='price-label'>Precio en oferta</div>
              <div className='price-main'>{Math.round(vehicle.preu)?.toLocaleString('es-ES')} €</div>
              <div className='price-financing'>Desde {Math.round(vehicle.preu / 60)}€/mes*</div>
              <div className='price-contact'>Precio al contado: {Math.round(vehicle.preu)?.toLocaleString('es-ES')}€</div>
            </div>

            <button className='interest-btn'>♥ Me interesa</button>

            <div className='financing-section'>
              <h3>Financiación a tu medida</h3>
              <div className='financing-box'>
                <div className='financing-header'>
                  <span className='promo-tag'>EN PROMOCIÓN</span>
                  <span className='old-price'>{Math.round(vehicle.preu * 1.2)?.toLocaleString('es-ES')}€</span>
                </div>
                <div className='financing-info'>
                  <span>Precio nuevo: {Math.round(vehicle.preu)?.toLocaleString('es-ES')} €</span>
                </div>
                <div className='financing-price'>
                  <span className='info-icon'>ⓘ</span>
                  <span>Precio final con las condiciones de financiación Flexicar</span>
                  <span className='final-price'>{Math.round(vehicle.preu)?.toLocaleString('es-ES')} €</span>
                </div>
                <button className='customize-financing'>
                  Personalizar financiación ∨<span className='monthly-price'>Desde {Math.round(vehicle.preu / 60)}€ al mes</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de pantalla completa */}
      {isFullscreen && (
        <div className='fullscreen-modal' onClick={closeFullscreen}>
          <button className='close-fullscreen' onClick={closeFullscreen}>
            ✕
          </button>

          <img src={currentImage} alt={`${vehicle.marca_nom} ${vehicle.model_nom}`} className='fullscreen-image' onClick={e => e.stopPropagation()} />

          {images.length > 1 && (
            <>
              <button
                className='nav-btn-fullscreen prev-btn-fullscreen'
                onClick={e => {
                  e.stopPropagation()
                  prevImageFullscreen()
                }}
              >
                ‹
              </button>
              <button
                className='nav-btn-fullscreen next-btn-fullscreen'
                onClick={e => {
                  e.stopPropagation()
                  nextImageFullscreen()
                }}
              >
                ›
              </button>
            </>
          )}

          {images.length > 1 && (
            <div className='image-counter'>
              {currentImageIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
